import React from "react";
import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Calendar, MapPin } from "lucide-react";
import { experience, education } from "../data/mock";

const TimelineItem = ({ item, index, type }) => {
  const isWork = type === "work";

  return (
    <motion.div
      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative"
    >
      {/* Timeline Dot - Hidden on mobile (767px and below) */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full border-4 border-[rgb(10,10,15)] z-10 hidden md:block" />

      {/* Content */}
      <div
        className={`flex items-center ${
          index % 2 === 0 ? "md:flex-row-reverse" : "md:flex-row"
        }`}
      >
        <div className="hidden md:block md:w-1/2" />
        <div className="w-full md:w-1/2 md:px-8">
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-[rgb(15,20,30)] rounded-xl p-6 border border-white/5 hover:border-blue-500/30 transition-all"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                {isWork ? (
                  <Briefcase className="text-blue-400" size={24} />
                ) : (
                  <GraduationCap className="text-blue-400" size={24} />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-1">
                  {isWork ? item.role : item.degree}
                </h3>
                <p className="text-blue-400 font-medium mb-2">
                  {isWork ? item.company : item.institution}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Calendar size={14} />
                  <span>{item.duration}</span>
                </div>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-4">
              {item.description}
            </p>
            {isWork && item.technologies && (
              <div className="flex flex-wrap gap-2">
                {item.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded-md text-xs font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const Experience = () => {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            My{" "}
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              Journey
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            A timeline of my professional experience and educational background
          </p>
        </motion.div>

        {/* Work Experience Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-12">
            <Briefcase className="text-blue-400" size={28} />
            <h2 className="text-3xl font-bold text-white">Work Experience</h2>
          </div>

          <div className="relative">
            {/* Timeline Line - Hidden on mobile (767px and below) */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-white/10 hidden md:block" />

            <div className="space-y-12">
              {experience.map((item, index) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  index={index}
                  type="work"
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Education Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center justify-center gap-3 mb-12">
            <GraduationCap className="text-blue-400" size={28} />
            <h2 className="text-3xl font-bold text-white">Education</h2>
          </div>

          <div className="relative">
            {/* Timeline Line - Hidden on mobile (767px and below) */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-white/10 hidden md:block" />

            <div className="space-y-12">
              {education.map((item, index) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  index={index}
                  type="education"
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Experience;
